import { mergeWith as objectMerge, isObjectLike } from 'lodash';
import goTo from 'vuetify/lib/services/goto';
import isUndefined from 'lodash/isUndefined';

export const moveElementInArray = (array, indexElement, offset) => {
  const element = array[indexElement];
  array.splice(indexElement, 1);
  array.splice(indexElement + offset, 0, element);
};

/**
 * ex: (1, 'номинаци', ['я', 'и', 'й']) -> 'номинация'
 * @param {String} word_base - base of word without ending
 * @param {Number} count - count of elements
 * @param {Array[String]} ending_variants - endings for base word
 * @returns {String} - base word with current ending
 */
export const wordsDeclensions = (word_base, count = 1, ending_variants = ['я', 'и', 'й']) => {
  let ending;
  const [end1, end2, end3] = ending_variants;
  if (count % 10 === 1 && count % 100 !== 11) {
    ending = end1;
  } else if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) {
    ending = end2;
  } else {
    ending = end3;
  }
  return `${word_base}${ending}`;
};

export const deepObjectMerge = (...objects) => {
  const base = {};
  const currentObjects = [...objects].filter((obj) => typeof obj === 'object');

  const mergeCustomerFn = (objectValue, sourceValue) => {
    if (Array.isArray(objectValue)) {
      return objectValue.concat(sourceValue);
    }
    return undefined;
  };

  return objectMerge(base, ...currentObjects, mergeCustomerFn);
};

export const checkObjectParams = (object, params = [], nullValue = null) => {
  params.forEach((param) => {
    if (object[param] == null) {
      object[param] = nullValue;
    }
  });
};

export const scrollTo = (target, offset = 50, options = { container: undefined }) => goTo(
  target,
  { duration: 500, offset },
).then(() => goTo(target, { duration: 200, offset, ...options }));

export const uniformDistribution = (array, requiredLength) => {
  if (array.length <= requiredLength) return array;
  const gap = array.length / requiredLength;
  const indexes = Array.from({ length: requiredLength - 1 }, (_, i) => Math.round(i * gap));
  return array.filter((_, i) => indexes.includes(i) || i === array.length - 1);
};

export const isObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Find different values of objects and return new object with keys of variant values;
 * New object get values from last object of params;
 * @note_1 empty objects ({}) convert to null;
 * @note_2 undefined values removes from object;
 * @ex { a: 1, b: 2 }, { a: 1, b: 3 } --> { b: 3 }
 * @param {...Object} objects fn(object1, object2, ..., objectN);
 * @return {Object | null} { objectN[keyM], objectN[keyN], ...} or null
 */
export const deepObjectsDifference = (...objects) => {
  if (objects?.length <= 1) {
    return objects[objects.length - 1] || null;
  }
  const keys = [...new Set(objects.map(Object.keys).flat())];
  const newObject = {};
  keys.forEach((key) => {
    const values = [...new Set(objects.map((object) => object[key]))];
    // if length > 1, then has different values;
    if (values?.length > 1) {
      let value = values[values.length - 1];
      if (isObject(value)) {
        const newParams = values.filter(isObject);
        value = deepObjectsDifference(...newParams);
      }
      newObject[key] = value;
    }
  });
  Object.entries(newObject).forEach(([key, value]) => {
    if (isUndefined(value)) {
      delete newObject[key];
    }
  });
  return Object.keys(newObject).length ? newObject : null;
};

/**
 * like lodash/set, but reverse functional.
 * ex: { c: 2, a: { b: 1, x: 9 }} --> { c: 2, 'a.b': 1, 'a.x': 9 }
 * @param {Object} object
 * @param {String} divider, default '.'
 * @return {Object} newObject
 */
export const flattenObject = (object, divider = '.') => Object.fromEntries(
  Object.entries(object).map(([key, value]) => {
    if (isObjectLike(value) && !Array.isArray(value)) {
      return Object.entries(
        flattenObject(value, divider),
      ).map(([newKey, newValue]) => [`${key}${divider}${newKey}`, newValue]);
    }
    return [[key, value]];
  }).flat(),
);

export const corvertQuery = (params) => {
  const query = Object.entries(params || {}).filter(([key, value]) => key && value).map(([key, value]) => [key, value].join('=')).join('&');
  return query ? `?${query}` : '';
};

export const printContent = (content, style = '') => {
  const w = window.open();
  const html = `<!DOCTYPE HTML>
    <html lang="en-us">
    <head>
      <title>Печатная страница</title>
      <style>${style}</style>
    </head>
    <body>
    ${content}
    </body>
  `;
  w.document.write(html);
  w.window.print();
  w.document.close();
};

export const isEventEditorRoute = (route) => !!route?.meta?.editor;

export const hasEventManagerPermission = ({
  permission,
  conf,
  user,
  event,
  eventManagerAccess,
}) => {
  const resolvedPermission = conf?.PERMISSIONS?.[permission] || permission;

  if (!resolvedPermission || !eventManagerAccess || !event?.id || !user?.hasPermissionsWithManyOptions) {
    return false;
  }

  return user.hasPermissionsWithManyOptions([resolvedPermission], [{ event_id: event.id }, {}], { all: false });
};

export const resolveEventLandingEditable = ({ route, user, event }) => (
  isEventEditorRoute(route)
  && !!event?.isOwner
  && !!(user?.isOrganization || user?.isAdmin)
);

export const resolveEventLandingManagerAccess = ({ route, user, event }) => (
  isEventEditorRoute(route)
  && !!user?.isEventManager
  && event?.owner_id === user?.eventManagerOrganizer?.user_id
);

export const resolveSectionEditAccess = ({
  permission,
  route,
  conf,
  user,
  event,
  isEditable = false,
  eventManagerAccess = false,
}) => {
  if (!isEventEditorRoute(route)) {
    return false;
  }

  if (isEditable) {
    return true;
  }

  if (!user?.isEventManager) {
    return false;
  }

  return hasEventManagerPermission({
    permission,
    conf,
    user,
    event,
    eventManagerAccess,
  });
};
